import { useEffect, useState } from 'react';
import api from '../utils/api';

/**
 * useRewardConfig — the admin-tunable store rewards (free-shipping threshold,
 * flat shipping rate, and the cart free-gift / mystery-gift ladder).
 *
 * These live on the single SiteSettings row and are edited in /admin/rewards.
 * The public read piggybacks on GET /api/site/status (also polled by the
 * maintenance gate). The values are cached at module scope so the cart, checkout
 * and navbar share one fetch per session.
 *
 * Defaults mirror the historic hardcoded constants, so every consumer renders
 * correctly on first paint and if the request ever fails.
 */
export const DEFAULT_REWARDS = {
  freeShippingThresholdCents: 5000,   // $50  → free shipping
  flatShippingRateCents:      699,    // $6.99 flat below the threshold
  freeGiftEnabled:            true,
  freeGiftThresholdCents:     15000,  // $150 → free balm stick
  freeGiftName:               'Free Bubblé balm stick',
  freeGiftValueCents:         2800,   // $28 retail value (struck-through)
  mysteryGiftThresholdCents:  30000,  // $300 → mystery wellness gift
  mysteryGiftName:            'Mystery wellness gift',
};

let cache = null;            // last successful fetch (shared across consumers)
let inflight = null;         // de-dupes concurrent first loads

async function loadRewards() {
  if (cache) return cache;
  if (!inflight) {
    inflight = api.get('/site/status')
      .then(({ data }) => {
        cache = { ...DEFAULT_REWARDS, ...(data?.rewards || {}) };
        return cache;
      })
      .catch(() => ({ ...DEFAULT_REWARDS }))
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function useRewardConfig() {
  const [rewards, setRewards] = useState(cache || DEFAULT_REWARDS);

  useEffect(() => {
    let active = true;
    loadRewards().then((r) => { if (active) setRewards(r); });
    return () => { active = false; };
  }, []);

  return rewards;
}
